import React, { useState } from 'react';
import { X, Save, Calendar, TrendingDown, Activity } from 'lucide-react';
import { GainsMonitorService } from '../services/gainsMonitorService';
import type { KaizenRecord } from '../types/gainsMonitor';

interface SnapshotFormProps {
  kaizen: KaizenRecord;
  onClose: () => void;
  onSuccess: () => void;
}

export const SnapshotForm: React.FC<SnapshotFormProps> = ({ kaizen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    measured_at: new Date().toISOString().split('T')[0],
    lead_time: kaizen.current_lead_time || kaizen.target_lead_time,
    cycle_time: kaizen.current_cycle_time || kaizen.target_cycle_time || 0,
    ftr: kaizen.current_ftr || kaizen.target_ftr || 100,
    capacity_fte: kaizen.current_capacity_fte || kaizen.target_capacity_fte || 0,
    throughput: 0,
    defect_rate: 0,
    rework_rate: 0,
    sample_size: 1,
    notes: ''
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await GainsMonitorService.addSnapshot(kaizen.id, {
        ...formData,
        measured_at: new Date(formData.measured_at).toISOString()
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar medição');
    } finally {
      setSaving(false);
    }
  };

  const calculateDeviation = (current: number, target: number) => {
    if (target === 0) return 0;
    return ((current - target) / target * 100).toFixed(1);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Activity className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Adicionar Medição</h2>
              <p className="text-sm text-blue-100">{kaizen.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Data da Medição */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Data da Medição
            </label>
            <input
              type="date"
              required
              value={formData.measured_at}
              onChange={(e) => setFormData({ ...formData, measured_at: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Métricas Principais */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded space-y-4">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-blue-600" />
              Métricas Principais
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Lead Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lead Time (min) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.lead_time}
                  onChange={(e) => setFormData({ ...formData, lead_time: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-gray-600">
                  Target: {kaizen.target_lead_time} min
                  <span className={`ml-2 font-semibold ${
                    parseFloat(calculateDeviation(formData.lead_time, kaizen.target_lead_time)) > 0 
                      ? 'text-red-600' 
                      : 'text-green-600'
                  }`}>
                    ({calculateDeviation(formData.lead_time, kaizen.target_lead_time)}%)
                  </span>
                </div>
              </div>

              {/* Cycle Time */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cycle Time (min)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cycle_time}
                  onChange={(e) => setFormData({ ...formData, cycle_time: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-gray-600">
                  Target: {kaizen.target_cycle_time || 0} min
                </div>
              </div>

              {/* FTR */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  FTR (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.ftr}
                  onChange={(e) => setFormData({ ...formData, ftr: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-gray-600">
                  Target: {kaizen.target_ftr || 100}%
                </div>
              </div>

              {/* Capacidade */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Capacidade (FTE)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.capacity_fte}
                  onChange={(e) => setFormData({ ...formData, capacity_fte: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
                <div className="mt-1 text-xs text-gray-600">
                  Target: {kaizen.target_capacity_fte || 0} FTE
                </div>
              </div>
            </div>
          </div>

          {/* Métricas Adicionais */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Métricas Adicionais</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Throughput (unidades/dia)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.throughput}
                  onChange={(e) => setFormData({ ...formData, throughput: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taxa de Defeitos (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.defect_rate}
                  onChange={(e) => setFormData({ ...formData, defect_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Taxa de Retrabalho (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.rework_rate}
                  onChange={(e) => setFormData({ ...formData, rework_rate: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tamanho da Amostra
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.sample_size}
                  onChange={(e) => setFormData({ ...formData, sample_size: parseInt(e.target.value) || 1 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Adicione observações sobre esta medição..."
              className="w-full px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Ações */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Salvando...' : 'Salvar Medição'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
